import { defineFeature } from '../define-feature';
import { TEST_IDS, optionTestId, sidebarNavTestId, sidebarSubTestId } from '../test-ids';

export default defineFeature({
  id: 'marketing.campaign.email.create',
  category: 'marketing',
  title: 'Créer une campagne email',
  intent:
    "Créer une campagne email marketing en définissant l'objet, en choisissant les destinataires (par tag client ou tous les clients abonnés) et l'expéditeur — la première étape indispensable avant de programmer l'envoi effectif.",
  context: {
    why: "Les campagnes email restent l'un des canaux marketing les plus rentables pour une entreprise de services, notamment parce qu'elles vous permettent de réactiver vos clients existants — ceux qui vous connaissent déjà et à qui vous n'avez pas besoin de tout expliquer. Une bonne campagne email peut générer plusieurs dizaines de rendez-vous supplémentaires en quelques jours, pour un coût pratiquement nul. Le principe de création d'une campagne dans Propelsia suit une logique en deux étapes distinctes qui protège votre communication d'envois précipités : la création (que vous êtes en train de faire) et la programmation (fiche dédiée). À la création, vous ne faites que poser les fondations de la campagne : son objet, ses destinataires, son expéditeur. Rien n'est envoyé à ce stade — c'est un brouillon que vous pourrez ensuite enrichir dans l'éditeur visuel avant de programmer l'envoi. Cette séparation entre création et envoi est un garde-fou important : elle vous évite les envois accidentels et vous laisse le temps de peaufiner votre message avant qu'il ne parte réellement dans les boîtes de vos clients.",
    use_cases: [
      "Vous préparez une offre spéciale saisonnière (soldes d'été, rentrée, Noël) : créez la campagne pour tous vos clients abonnés au marketing.",
      'Vous voulez cibler uniquement vos clients fidèles avec une offre VIP : créez la campagne avec le filtre du tag « Fidèle ».',
      'Vous lancez une nouvelle prestation et vous voulez la faire connaître à vos clients existants : ils sont déjà chauds, la conversion sera meilleure.',
      'Vous voulez récupérer des clients dormants qui ne sont plus venus depuis six mois : ciblez ce segment avec un tag spécifique.',
      "Vous testez une nouvelle formulation d'objet ou de contenu : créez plusieurs brouillons pour vous décider avant l'envoi.",
    ],
  },
  required_scopes: ['campaign.email.create'],
  prerequisites: [
    {
      ref: 'customers.customer.create',
      requirement: 'recommended',
      why: "Avoir des clients en base (avec consentement marketing) est nécessaire pour l'envoi, pas pour la création : un brouillon de campagne peut être créé sans destinataire.",
    },
  ],
  ui_entry_points: [{ route: '/marketing/campaigns', element: 'Bouton « Nouvelle campagne email »' }],
  api: {
    primary_operation_id: 'ProCampaignEmailAppController_createProCampaignEmail',
    related_operation_ids: [
      'ProTagAppController_getProTag',
      'ProCampaignAppController_getProCampaign',
      'FieldAssistAppController_fieldAction',
    ],
  },
  steps: [
    'Dans l’éditeur, ouvrez « Prévisualiser avec un client » pour vérifier l’objet et le corps avec ses réponses personnalisées. Cela ne change pas le ciblage et n’envoie aucun email.',
    "Rédiger l'objet de l'email (accrocheur, sans spam).",
    'Choisir les destinataires : un ou plusieurs tags clients, ou laisser vide pour cibler tous les clients abonnés.',
    'Valider la création — la campagne est créée en brouillon, prête à être programmée.',
  ],
  gotchas: [
    "L'objet est le seul texte que Pulse rédige de façon fiable (champ AI `EMAIL_SUBJECT`). Le **corps de l'email est un blob du builder visuel** : Pulse ne le génère PAS — la campagne part avec le contenu par défaut du template, le pro le peaufine ensuite dans l'éditeur email.",
    "Destinataires = `pro_tags` (liste d'ids de tags). Laisser le tableau vide cible tous les clients abonnés au marketing. Pour cibler un tag, lire d'abord la liste des tags (`getProTag`) pour récupérer le bon id.",
    "La création ne fait qu'un brouillon : rien n'est envoyé. L'envoi se programme séparément (`scheduleCampaign`).",
    'Seuls les clients ayant accepté les emails marketing sont comptés comme destinataires — le nombre réel peut être inférieur à la taille du tag.',
  ],
  related_features: ['marketing.campaign.email.schedule', 'customers.customer.create'],
  demo_script: {
    duration_target_s: 35,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'L’e-mail réactive vos clients existants à très faible coût — créons une campagne.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/marketing'),
        caption: 'Dans le menu latéral, ouvrez « Communication ».',
      },
      { action: 'click', target: sidebarSubTestId('/marketing/campaigns'), caption: 'Puis « Campagne marketing ».' },
      {
        action: 'click',
        target: TEST_IDS.campaigns_create,
        caption: 'Cliquez sur « Créer une campagne ».',
      },
      {
        action: 'click',
        // La première option du menu de création est « Campagne email ».
        target: optionTestId(TEST_IDS.campaigns_create, 0),
        caption: 'Choisissez « Campagne email ».',
      },
      {
        action: 'wait_for',
        target: TEST_IDS.campaign_subject,
        caption: 'L’éditeur s’ouvre sur l’objet et les destinataires.',
      },
      {
        action: 'fill',
        target: TEST_IDS.campaign_subject,
        value: 'Une offre spéciale pour vous',
        caption:
          'Rédigez l’objet : c’est lui qui décide si votre e-mail sera ouvert. Vous obtenez un brouillon, rien n’est encore envoyé.',
        capture: true,
      },
    ],
  },
});
